import React from 'react';
import { Box, Button, Card, CardContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type GuidedTourCardProps = {
  title: string;
  body: string;
  stepOrdinal: number;
  stepCount: number;
  isFirst?: boolean;
  isLast?: boolean;
  nextLabel?: string;
  prevLabel?: string;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  /** Flecha hacia el target (estilo referencia). */
  arrow?: 'top' | 'bottom' | 'left' | 'right' | 'none';
  /** Distancia (px) de la flecha al borde izquierdo (flecha arriba/abajo) o superior (izquierda/derecha). */
  arrowOffset?: number;
  /** Ref opcional para medir tamaño. */
  cardRef?: React.Ref<HTMLDivElement>;
  className?: string;
  sx?: object;
};

const TITLE_COLOR = '#4fc3f7';

/**
 * Tarjeta de tour guiado reutilizable (estilo onboarding tipo “visita por la app”).
 * Usar en overlay de product tour u otras funcionalidades de guía.
 */
const GuidedTourCard: React.FC<GuidedTourCardProps> = ({
  title,
  body,
  stepOrdinal,
  stepCount,
  isFirst = false,
  isLast = false,
  nextLabel,
  prevLabel = 'Anterior',
  onClose,
  onNext,
  onPrev,
  arrow = 'none',
  arrowOffset = 35,
  cardRef,
  className,
  sx,
}) => {
  const primaryLabel = nextLabel ?? (isLast ? 'Finalizar' : 'Siguiente');

  const arrowSx =
    arrow === 'none'
      ? null
      : {
          content: '""',
          position: 'absolute' as const,
          width: 14,
          height: 14,
          bgcolor: '#fff',
          transform: 'rotate(45deg)',
          border: '1px solid',
          borderColor: 'divider',
          zIndex: 0,
          ...(arrow === 'bottom'
            ? { bottom: -7, left: arrowOffset - 7, borderTop: 'none', borderLeft: 'none' }
            : arrow === 'top'
              ? { top: -7, left: arrowOffset - 7, borderBottom: 'none', borderRight: 'none' }
              : arrow === 'left'
                ? { left: -7, top: arrowOffset - 7, borderTop: 'none', borderRight: 'none' }
                : { right: -7, top: arrowOffset - 7, borderBottom: 'none', borderLeft: 'none' }),
        };

  return (
    <Card
      ref={cardRef}
      elevation={0}
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 360,
        borderRadius: 2,
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 28px rgba(15, 23, 42, 0.14)',
        overflow: 'visible',
        '&::before': arrowSx ?? undefined,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: TITLE_COLOR, lineHeight: 1.3, pr: 1 }}
          >
            {title}
          </Typography>
          <IconButton
            size="small"
            aria-label="Cerrar tour"
            onClick={onClose}
            sx={{ mt: -0.5, mr: -0.5, color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.primary', mb: 2.25, lineHeight: 1.55 }}>
          {body}
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {stepOrdinal} of {Math.max(stepCount, stepOrdinal)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={isFirst}
              onClick={onPrev}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                borderColor: 'grey.400',
                bgcolor: '#fff',
                borderRadius: 1,
                px: 1.5,
                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
              }}
            >
              {prevLabel}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={onNext}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                borderColor: 'grey.700',
                bgcolor: '#fff',
                borderRadius: 1,
                px: 1.5,
                fontWeight: 600,
                '&:hover': { borderColor: 'grey.900', bgcolor: 'grey.50' },
              }}
            >
              {primaryLabel}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default GuidedTourCard;
